<script setup lang="ts">
import { inject, ref, watch, nextTick } from 'vue';
import { NIcon } from 'naive-ui';
import { IconDocument, IconDocuments, IconFolder, IconSearch } from '@/components/design/icons';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import type { GlobalSearchReturn, SearchTab } from '@/composables/useGlobalSearch';
import { contractRoute, vaultDirectoryRoute } from '@/router/links';

const search = inject<GlobalSearchReturn>('globalSearch');
if (!search) {
  throw new Error(
    'GlobalSearchModal must be used within a component tree that provides globalSearch'
  );
}

const inputRef = ref<HTMLInputElement | null>(null);

// Autofocus input when modal opens
watch(
  () => search.isOpen.value,
  async open => {
    if (open) {
      await nextTick();
      inputRef.value?.focus();
    }
  }
);

const handleTabChange = (tab: string): void => {
  search.activeTab.value = tab as SearchTab;
};

// Results are real links; a plain click navigates in-app and closes the
// modal, while a modified click opens a new tab and leaves the search open.
const closeOnNavigate = (e: MouseEvent): void => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  search.close();
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isSearchActive = ref(false);
watch(
  () => search.searchText.value,
  val => {
    isSearchActive.value = val.length >= 2;
  }
);
</script>

<template>
  <NModal
    :show="search.isOpen.value"
    :mask-closable="true"
    :mask-style="{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.35)' }"
    transform-origin="center"
    @update:show="
      (val: boolean) => {
        if (!val) search.close();
      }
    "
  >
    <div class="spotlight-card">
      <!-- Search Input -->
      <div class="spotlight-input-wrapper">
        <NIcon :size="20" class="spotlight-search-icon">
          <IconSearch />
        </NIcon>
        <input
          ref="inputRef"
          :value="search.searchText.value"
          class="spotlight-input"
          placeholder="Search contracts, directories, and files..."
          @input="search.searchText.value = ($event.target as HTMLInputElement).value"
        />
        <span v-if="search.searchText.value.length === 0" class="spotlight-shortcut">
          <kbd>esc</kbd>
        </span>
      </div>

      <!-- Guidance (before searching) -->
      <div v-if="!isSearchActive" class="spotlight-guidance">
        <NIcon :size="32" style="color: #cbd5e1; margin-bottom: 8px">
          <IconSearch />
        </NIcon>
        <p>Type at least 2 characters to search...</p>
      </div>

      <!-- Tabs + Results -->
      <template v-if="isSearchActive">
        <NTabs
          type="line"
          :value="search.activeTab.value"
          size="small"
          class="spotlight-tabs"
          @update:value="handleTabChange"
        >
          <NTabPane name="contracts" :tab="`Contracts (${search.contracts.total.value})`">
            <div
              v-if="search.contracts.loading.value && search.contracts.items.value.length === 0"
              class="spotlight-loading"
            >
              <NSpin size="small" />
            </div>
            <NEmpty
              v-else-if="search.contracts.items.value.length === 0"
              description="No contracts found"
              size="small"
              style="margin: 32px 0"
            />
            <NInfiniteScroll
              v-else
              class="spotlight-results"
              :distance="20"
              :finished="!search.contracts.hasMore.value"
              @load="search.loadMore()"
            >
              <router-link
                v-for="item in search.contracts.items.value"
                :key="item.uid"
                :to="contractRoute(item.uid)"
                class="spotlight-result-row"
                @click="closeOnNavigate"
              >
                <div class="result-icon result-icon--contract">
                  <NIcon :size="16"><IconDocuments /></NIcon>
                </div>
                <div class="result-body">
                  <div class="result-title-row">
                    <span class="result-title">{{ item.name }}</span>
                    <NTag
                      v-if="item.contract_type"
                      size="tiny"
                      :bordered="false"
                      class="result-type-tag"
                      >{{ item.contract_type.name }}</NTag
                    >
                  </div>
                  <p class="result-description">
                    <span v-if="item.owner_user?.full_name"
                      >{{ item.owner_user.full_name }} ·
                    </span>
                    <span
                      >{{ item.contract_file_count }}
                      {{ item.contract_file_count === 1 ? 'file' : 'files' }}</span
                    >
                  </p>
                </div>
                <span class="result-date">{{ formatDateTimeForTable(item.created_at) }}</span>
              </router-link>
            </NInfiniteScroll>
          </NTabPane>

          <NTabPane name="directories" :tab="`Directories (${search.directories.total.value})`">
            <div
              v-if="search.directories.loading.value && search.directories.items.value.length === 0"
              class="spotlight-loading"
            >
              <NSpin size="small" />
            </div>
            <NEmpty
              v-else-if="search.directories.items.value.length === 0"
              description="No directories found"
              size="small"
              style="margin: 32px 0"
            />
            <NInfiniteScroll
              v-else
              class="spotlight-results"
              :distance="20"
              :finished="!search.directories.hasMore.value"
              @load="search.loadMore()"
            >
              <router-link
                v-for="item in search.directories.items.value"
                :key="item.uid"
                :to="vaultDirectoryRoute(item.uid)"
                class="spotlight-result-row"
                @click="closeOnNavigate"
              >
                <div class="result-icon result-icon--directory">
                  <NIcon :size="16"><IconFolder /></NIcon>
                </div>
                <div class="result-body">
                  <div class="result-title-row">
                    <span class="result-title">{{ item.directory_name }}</span>
                  </div>
                  <p v-if="item.owner_user?.full_name" class="result-description">
                    {{ item.owner_user.full_name }}
                  </p>
                </div>
                <span class="result-date">{{ formatDateTimeForTable(item.created_at) }}</span>
              </router-link>
            </NInfiniteScroll>
          </NTabPane>

          <NTabPane name="files" :tab="`Files (${search.files.total.value})`">
            <div
              v-if="search.files.loading.value && search.files.items.value.length === 0"
              class="spotlight-loading"
            >
              <NSpin size="small" />
            </div>
            <NEmpty
              v-else-if="search.files.items.value.length === 0"
              description="No files found"
              size="small"
              style="margin: 32px 0"
            />
            <NInfiniteScroll
              v-else
              class="spotlight-results"
              :distance="20"
              :finished="!search.files.hasMore.value"
              @load="search.loadMore()"
            >
              <router-link
                v-for="item in search.files.items.value"
                :key="item.uid"
                :to="{ name: 'vault-file-versions', params: { uid: item.uid } }"
                class="spotlight-result-row"
                @click="closeOnNavigate"
              >
                <div class="result-icon result-icon--file">
                  <NIcon :size="16"><IconDocument /></NIcon>
                </div>
                <div class="result-body">
                  <div class="result-title-row">
                    <span class="result-title">{{
                      item.current_revision?.file_name || 'Unknown File'
                    }}</span>
                  </div>
                  <p class="result-description">
                    <span v-if="item.parent_directory?.directory_name">{{
                      item.parent_directory.directory_name
                    }}</span>
                    <span
                      v-if="item.owner_user?.full_name && item.parent_directory?.directory_name"
                    >
                      ·
                    </span>
                    <span v-if="item.owner_user?.full_name">{{ item.owner_user.full_name }}</span>
                    <span v-if="item.current_revision?.size_b">
                      · {{ formatFileSize(item.current_revision.size_b) }}</span
                    >
                  </p>
                </div>
                <span class="result-date">{{ formatDateTimeForTable(item.created_at) }}</span>
              </router-link>
            </NInfiniteScroll>
          </NTabPane>
        </NTabs>
      </template>
    </div>
  </NModal>
</template>

<style scoped>
/* ===== Spotlight Card ===== */
.spotlight-card {
  width: 680px;
  max-width: 90vw;
  margin-top: 12vh;
  margin-left: auto;
  margin-right: auto;
  background: white;
  border-radius: 14px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ===== Input ===== */
.spotlight-input-wrapper {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;
}

.spotlight-search-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

.spotlight-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #1e293b;
  background: transparent;
  font-family: inherit;
}

.spotlight-input::placeholder {
  color: #94a3b8;
}

.spotlight-shortcut {
  flex-shrink: 0;
}

.spotlight-shortcut kbd {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-family: inherit;
  color: #94a3b8;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

/* ===== Guidance ===== */
.spotlight-guidance {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #94a3b8;
}

.spotlight-guidance p {
  margin: 0;
  font-size: 14px;
}

/* ===== Tabs ===== */
.spotlight-tabs {
  padding: 0 4px;
}

.spotlight-tabs :deep(.n-tabs-nav) {
  padding: 0 16px;
}

.spotlight-tabs :deep(.n-tab-pane) {
  padding: 0 !important;
}

/* ===== Results ===== */
.spotlight-results {
  max-height: 50vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.spotlight-results::-webkit-scrollbar {
  width: 6px;
}

.spotlight-results::-webkit-scrollbar-track {
  background: transparent;
}

.spotlight-results::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.spotlight-result-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  transition: background 0.1s ease;
  border-bottom: 1px solid #f8fafc;
}

.spotlight-result-row:last-of-type {
  border-bottom: none;
}

.spotlight-result-row:hover {
  background: #f8fafc;
}

/* ===== Result icon ===== */
.result-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.result-icon--contract {
  background: #eff6ff;
  color: #2563eb;
}

.result-icon--directory {
  background: #faf5ff;
  color: #7c3aed;
}

.result-icon--file {
  background: #f0fdf4;
  color: #16a34a;
}

/* ===== Result body ===== */
.result-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.result-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-title {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-type-tag {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}

.result-description {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-date {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 3px;
}

/* ===== Loading ===== */
.spotlight-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}
</style>
